import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowLeft01Icon,
  Call02Icon,
  Search01Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  voiceApi,
  type StartTransferPayload,
  type TargetedTransferMode,
  type TransferListEntry,
  type TransferTargetsResponse
} from "api/voice";
import { useAuthContext } from "providers/Auth";
import { Avatar } from "shared/components/Avatar";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { cx } from "shared/utils";
import { formatPhoneDisplay } from "../views/phone-numbers/lib/format";
import { firstName, useSoftphone } from "./SoftphoneProvider";

// What the picker is for: Transfer (CRM-1747), or Add call (CRM-1818), which
// rings the person picked right away.
export type TransferPanelPurpose = "transfer" | "add";

// The destination picked in the Transfer call view (CRM-1747).
interface Destination {
  payload: StartTransferPayload;
  name: string;
  // A Transfer list entry's note, for the card that follows.
  note?: string | null;
  // "Rings in Boom", "(916) 555-0110 · Transfer list" or "Typed number".
  sub: string;
}

const TEAM_SUB = "Rings in Boom";
const TYPED_SUB = "Typed number";
const NO_MATCHES_TYPED_OFF = "No matches.";

// The words that differ between Transfer and Add call.
const PURPOSE_COPY: Record<
  TransferPanelPurpose,
  {
    title: string;
    typedRow: (number: string) => string;
    noMatches: string;
    loadFailed: string;
  }
> = {
  transfer: {
    title: "Transfer call",
    typedRow: (number) => `Transfer to ${number}`,
    noMatches: "No matches. Type a full phone number to transfer to it.",
    loadFailed: "Couldn't load who you can transfer to. Close and try again."
  },
  add: {
    title: "Add to call",
    typedRow: (number) => `Add ${number}`,
    noMatches: "No matches. Type a full phone number to add it.",
    loadFailed: "Couldn't load who you can add. Close and try again."
  }
};

// Admins add entries on Phone numbers › Transfer list. A new tab: a same-tab
// navigation would unload the SPA and drop the live call.
const TRANSFER_LIST_SETTINGS_URL = "/phone-numbers?tab=transfer-list";

const LETTER_RE = /[a-z]/i;

// A full US number typed in any shape: 10 digits, or 11 starting with 1. The
// area code never starts with 0 or 1; the API has the final word on whether
// it is a real US one.
const typedUsNumber = (query: string): string | null => {
  const digits = query.replace(/\D/g, "");
  const national =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (national.length !== 10 || /^[01]/.test(national)) return null;
  return `+1${national}`;
};

const matchesName = (name: string | null, query: string) =>
  (name ?? "").toLowerCase().includes(query);

interface Filtered {
  team: TransferTargetsResponse["team"];
  list: TransferListEntry[];
  typed: string | null;
}

// Names filter both sections; digits narrow the Transfer list by number and
// offer the typed number itself, first.
const filterTargets = (
  targets: TransferTargetsResponse | undefined,
  rawQuery: string
): Filtered => {
  if (!targets) return { team: [], list: [], typed: null };
  const query = rawQuery.trim().toLowerCase();
  if (!query)
    return { team: targets.team, list: targets.transfer_list, typed: null };

  if (LETTER_RE.test(query))
    return {
      team: targets.team.filter((user) => matchesName(user.name, query)),
      list: targets.transfer_list.filter(
        (entry) =>
          matchesName(entry.name, query) || matchesName(entry.note, query)
      ),
      typed: null
    };

  const digits = query.replace(/\D/g, "");
  return {
    team: [],
    list: targets.transfer_list.filter((entry) =>
      entry.phone_number.replace(/\D/g, "").includes(digits)
    ),
    typed: targets.typed_numbers_allowed ? typedUsNumber(query) : null
  };
};

export function TransferPanel({
  purpose,
  callerName,
  elapsedLabel,
  onClose
}: {
  purpose: TransferPanelPurpose;
  // How the card names the other party ("Maria Lopez" or their number).
  callerName: string;
  elapsedLabel: string;
  // Back out of the view, or a transfer started from it.
  onClose: () => void;
}) {
  const { startTransfer } = useSoftphone();
  const [query, setQuery] = useState("");
  const [destination, setDestination] = useState<Destination | null>(null);
  // Add call: who is being rung, and why it didn't start.
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const copy = PURPOSE_COPY[purpose];

  const targetsQuery = useQuery({
    queryKey: ["CALL_TRANSFER", "TARGETS"],
    queryFn: voiceApi.getTransferTargets,
    staleTime: 30_000
  });
  const targets = targetsQuery.data;
  const filtered = useMemo(
    () => filterTargets(targets, query),
    [targets, query]
  );

  // Add call has no Talk first / Transfer now choice: picking rings them.
  const add = async (picked: Destination) => {
    setAdding(true);
    setAddError(null);
    try {
      await startTransfer(
        picked.payload,
        { name: picked.name, note: picked.note },
        "add"
      );
      onClose();
    } catch (err) {
      setAddError(err instanceof Error ? err.message : String(err));
      setAdding(false);
    }
  };

  if (destination)
    return (
      <TransferConfirm
        callerName={callerName}
        destination={destination}
        onBack={() => setDestination(null)}
        onStarted={onClose}
      />
    );

  return (
    <div className="w-full text-left">
      <PanelHeader
        title={copy.title}
        subtitle={`${callerName} · ${elapsedLabel}`}
        onBack={onClose}
      />

      <label className="mt-4 flex items-center gap-2 rounded-lg px-3 py-2 ring-1 ring-alpha-100 focus-within:ring-2 focus-within:ring-primary-500">
        <HugeiconsIcon
          icon={Search01Icon}
          size={18}
          className="text-content-300"
        />
        <input
          // The list is the point of this view: start typing straight away.
          autoFocus
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={
            targets?.typed_numbers_allowed === false
              ? "Search your team and Transfer list"
              : "Search or type a number"
          }
          aria-label="Search or type a number"
          className="w-full border-0 bg-transparent p-0 text-sm text-content-1000 placeholder:text-content-300 focus:outline-none focus:ring-0"
        />
      </label>
      {purpose === "add" ? (
        <p className="mt-2 text-xs text-content-400">
          {firstName(callerName)} goes on hold while their phone rings. Talk to
          them first, then Merge.
        </p>
      ) : null}
      {addError ? (
        <p className="mt-2 text-xs text-danger-500">{addError}</p>
      ) : null}

      <div className="mt-3 max-h-[320px] space-y-4 overflow-y-auto">
        <TargetsBody
          loading={targetsQuery.isLoading}
          failed={targetsQuery.isError}
          purpose={purpose}
          targets={targets}
          filtered={filtered}
          disabled={adding}
          onPick={
            purpose === "add" ? (picked) => void add(picked) : setDestination
          }
        />
      </div>
    </div>
  );
}

function TargetsBody({
  loading,
  failed,
  ...sections
}: {
  loading: boolean;
  failed: boolean;
  purpose: TransferPanelPurpose;
  targets: TransferTargetsResponse | undefined;
  filtered: Filtered;
  // While an Add call starts.
  disabled: boolean;
  onPick: (destination: Destination) => void;
}) {
  if (loading)
    return (
      <p className="py-6 text-center text-sm text-content-400">Loading…</p>
    );
  if (failed)
    return (
      <p className="py-6 text-center text-sm text-danger-500">
        {PURPOSE_COPY[sections.purpose].loadFailed}
      </p>
    );
  return <TargetSections {...sections} />;
}

function TargetSections({
  purpose,
  targets,
  filtered,
  disabled,
  onPick
}: {
  purpose: TransferPanelPurpose;
  targets: TransferTargetsResponse | undefined;
  filtered: Filtered;
  disabled: boolean;
  onPick: (destination: Destination) => void;
}) {
  const { type } = useAuthContext();
  const { typed } = filtered;
  const copy = PURPOSE_COPY[purpose];
  const nothing =
    !typed && filtered.team.length === 0 && filtered.list.length === 0;
  const listEmpty = (targets?.transfer_list.length ?? 0) === 0;

  return (
    <>
      {typed ? (
        <TargetRow
          name={copy.typedRow(formatPhoneDisplay(typed))}
          sub={TYPED_SUB}
          icon
          disabled={disabled}
          onClick={() =>
            onPick({
              payload: { target_kind: "typed", phone_number: typed },
              name: formatPhoneDisplay(typed),
              sub: TYPED_SUB
            })
          }
        />
      ) : null}

      {filtered.team.length > 0 ? (
        <section>
          <SectionTitle>Team</SectionTitle>
          {filtered.team.map((user) => {
            const name = user.name?.trim() || "Teammate";
            return (
              <TargetRow
                key={user.id}
                name={name}
                sub={TEAM_SUB}
                disabled={disabled}
                onClick={() =>
                  onPick({
                    payload: {
                      target_kind: "team",
                      target_partner_user_id: user.id
                    },
                    name,
                    sub: TEAM_SUB
                  })
                }
              />
            );
          })}
        </section>
      ) : null}

      {filtered.list.length > 0 ? (
        <section>
          <SectionTitle>Transfer list</SectionTitle>
          {filtered.list.map((entry) => {
            const number = formatPhoneDisplay(entry.phone_number);
            return (
              <TargetRow
                key={entry.id}
                name={entry.name}
                sub={entry.note ? `${entry.note} · ${number}` : number}
                disabled={disabled}
                onClick={() =>
                  onPick({
                    payload: {
                      target_kind: "list",
                      transfer_list_entry_id: entry.id
                    },
                    name: entry.name,
                    note: entry.note,
                    sub: `${number} · Transfer list`
                  })
                }
              />
            );
          })}
        </section>
      ) : null}

      {nothing ? (
        <p className="py-6 text-center text-sm text-content-400">
          {targets?.typed_numbers_allowed === false
            ? NO_MATCHES_TYPED_OFF
            : copy.noMatches}
        </p>
      ) : null}

      {listEmpty && type.adminPrivileges ? (
        <a
          href={TRANSFER_LIST_SETTINGS_URL}
          target="_blank"
          rel="noreferrer"
          className="block text-center text-sm font-medium text-primary-500 hover:text-primary-600"
        >
          Add numbers to your Transfer list
        </a>
      ) : null}
    </>
  );
}

function TransferConfirm({
  callerName,
  destination,
  onBack,
  onStarted
}: {
  callerName: string;
  destination: Destination;
  onBack: () => void;
  onStarted: () => void;
}) {
  const { startTransfer } = useSoftphone();
  const [sending, setSending] = useState<TargetedTransferMode | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = async (mode: TargetedTransferMode) => {
    setSending(mode);
    setError(null);
    try {
      await startTransfer(
        destination.payload,
        { name: destination.name, note: destination.note },
        mode
      );
      onStarted();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSending(null);
    }
  };

  return (
    <div className="w-full text-left">
      <PanelHeader title="Transfer call" onBack={onBack} />
      <p className="mt-4 text-sm text-content-400">Transfer {callerName} to</p>
      <div className="mt-2 flex items-center gap-3 rounded-xl bg-alpha-40 p-3">
        <Avatar name={destination.name} size={40} />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-content-1000">
            {destination.name}
          </p>
          <p className="truncate text-xs text-content-400">{destination.sub}</p>
        </div>
      </div>
      <p className="mt-3 text-xs text-content-400">
        Talk first puts {callerName} on hold while you check{" "}
        {firstName(destination.name)} can take the call. Transfer now hands them
        over right away.
      </p>
      {error ? <p className="mt-3 text-xs text-danger-500">{error}</p> : null}
      <div className="mt-5 space-y-2">
        <Button
          className="w-full"
          size={ButtonSize.BASE}
          loading={sending === "talk_first"}
          disabled={sending !== null}
          onClick={() => void send("talk_first")}
        >
          Talk first
        </Button>
        <Button
          className="w-full"
          size={ButtonSize.BASE}
          variant={ButtonVariant.BASE}
          loading={sending === "now"}
          disabled={sending !== null}
          onClick={() => void send("now")}
        >
          Transfer now
        </Button>
        <Button
          className="w-full"
          size={ButtonSize.BASE}
          variant={ButtonVariant.TEXT}
          disabled={sending !== null}
          onClick={onBack}
        >
          Back to the list
        </Button>
      </div>
    </div>
  );
}

function PanelHeader({
  title,
  subtitle,
  onBack
}: {
  title: string;
  subtitle?: string;
  onBack: () => void;
}) {
  return (
    <div className="flex items-start gap-2 pr-8">
      <button
        type="button"
        aria-label="Back"
        onClick={onBack}
        className="grid size-8 shrink-0 place-items-center rounded-full text-content-400 transition hover:bg-alpha-40 hover:text-content-1000"
      >
        <HugeiconsIcon icon={ArrowLeft01Icon} size={20} />
      </button>
      <div className="min-w-0 pt-1">
        <p className="text-base font-semibold text-content-1000">{title}</p>
        {subtitle ? (
          <p className="truncate text-xs text-content-400">{subtitle}</p>
        ) : null}
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: string }) {
  return (
    <p className="mb-1 px-1 text-xs font-medium uppercase tracking-wide text-content-300">
      {children}
    </p>
  );
}

function TargetRow({
  name,
  sub,
  icon,
  disabled,
  onClick
}: {
  name: string;
  sub: string;
  // A typed number has no person to make initials of.
  icon?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-3 rounded-lg px-1 py-2 text-left transition hover:bg-alpha-40 disabled:cursor-not-allowed disabled:opacity-40"
    >
      {icon ? (
        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-alpha-40 text-content-1000">
          <HugeiconsIcon icon={Call02Icon} size={18} />
        </span>
      ) : (
        <Avatar name={name} size={36} />
      )}
      <span className="min-w-0">
        <span
          className={cx(
            "block truncate text-sm text-content-1000",
            icon ? "font-semibold" : "font-medium"
          )}
        >
          {name}
        </span>
        <span className="block truncate text-xs text-content-400">{sub}</span>
      </span>
    </button>
  );
}
